import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Link } from 'react-router-dom';

import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { getOpsCases, getOpsClasses, getOpsPrograms } from '../../../requests';
import { getSyncStatus } from '../../../requests/opsSync';
import { cardClass } from '../ui';

const CARDS = [
  {
    to: PATH.opsCalendar,
    title: 'Delivery calendar',
    text: 'What is running, when, who is teaching, what clashes',
  },
  {
    to: PATH.opsClasses,
    title: 'Classes',
    text: 'Create and edit classes; search all 13,000+',
  },
  {
    to: PATH.opsAssignments,
    title: 'Instructor assignments',
    text: 'Who teaches what, on which contracted rates',
  },
  {
    to: PATH.opsRegistrations,
    title: 'Registrations',
    text: 'Rosters, add people, attendance and cancellations',
  },
  {
    to: PATH.opsContacts,
    title: 'Contacts',
    text: 'The person-record everything else hangs off',
  },
  {
    to: PATH.opsAccounts,
    title: 'Accounts',
    text: 'The company 360 — programs, classes, cases, people',
  },
  {
    to: PATH.opsCatalogue,
    title: 'Course catalogue',
    text: 'Products, families, what the website sells',
  },
  {
    to: PATH.opsPrograms,
    title: 'Programs / Team Pass',
    text: 'Balances, members, consumption',
  },
  {
    to: PATH.opsCases,
    title: 'CX inbox',
    text: 'The queue — triage, route, close',
  },
];

const STAT_CARD =
  'group rounded-xl border border-white-regular bg-white px-5 py-4 transition-all hover:-translate-y-0.5 hover:border-blue-500 hover:shadow-light';

const OpsHome: React.FC = () => {
  const { data: cases } = useQuery({
    queryKey: ['opsCases', '', '', false, 1],
    queryFn: () => getOpsCases({ page: 1 }),
  });
  const { data: programs } = useQuery({
    queryKey: ['opsPrograms', '', 'Utilization', 1],
    queryFn: () => getOpsPrograms({ status: 'Utilization', page: 1 }),
  });
  const { data: classes } = useQuery({
    queryKey: ['opsClasses', '', 'Available', '', 1],
    queryFn: () => getOpsClasses({ status: 'Available', page: 1 }),
  });

  const { data: sync } = useQuery({
    queryKey: ['opsSync', 'status'],
    queryFn: getSyncStatus,
  });

  const stats = [
    { to: PATH.opsCases, n: cases?.total, label: 'open cases' },
    {
      to: PATH.opsPrograms,
      n: programs?.total,
      label: 'programs in utilization',
    },
    { to: PATH.opsClasses, n: classes?.total, label: 'available classes' },
  ];

  return (
    <div className="mx-auto max-w-5xl px-8 pb-24">
      <h1 className="pb-1 pt-8 text-2xl font-bold tracking-tight text-gray-four">
        Operations
      </h1>
      <p className="pb-6 text-sm text-gray-three">
        Learn It's delivery operations, running on the portal's own data.
      </p>
      {sync && (
        <Link
          to={PATH.opsSync}
          className={`mb-6 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border px-5 py-3 text-sm ${
            sync.stale ||
            sync.lastRun?.status === 'failed' ||
            sync.lastRun?.status === 'partial' ||
            sync.openConflicts > 0
              ? 'border-red-300 bg-red-50 text-red-800'
              : 'border-white-regular bg-white text-gray-three'
          }`}
        >
          <span>
            <span className="text-[11px] font-semibold uppercase tracking-wider">
              Data as of
            </span>{' '}
            <b>
              {sync.dataAsOf
                ? new Date(sync.dataAsOf).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })
                : 'no nightly run yet'}
            </b>
          </span>
          {sync.lastRun?.running && <span>Sync running now</span>}
          {sync.lastRun?.status === 'failed' && <span>Last sync failed</span>}
          {sync.lastRun?.status === 'partial' && (
            <span>
              Last sync: <b>{sync.lastRun.failedObjects.length}</b> object
              {sync.lastRun.failedObjects.length === 1 ? '' : 's'} did not
              refresh
            </span>
          )}
          {sync.stale && sync.dataAsOf && <span>Over 36 h old</span>}
          {sync.openConflicts > 0 && (
            <span>
              <b>{sync.openConflicts}</b> conflict
              {sync.openConflicts === 1 ? '' : 's'} to resolve
            </span>
          )}
          <span className="ml-auto text-xs">Salesforce sync →</span>
        </Link>
      )}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {stats.map((s) => (
          <Link key={s.label} to={s.to} className={STAT_CARD}>
            <div className="text-3xl font-bold text-gray-four [font-variant-numeric:tabular-nums]">
              {s.n?.toLocaleString() ?? '…'}
            </div>
            <div className="mt-0.5 text-sm text-gray-three group-hover:text-blue-primary">
              {s.label}
            </div>
          </Link>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CARDS.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className={`${cardClass} group p-5 transition-all hover:-translate-y-0.5 hover:border-blue-500 hover:shadow-light`}
          >
            <div className="font-semibold text-gray-four group-hover:text-blue-primary">
              {c.title}
            </div>
            <div className="mt-1 text-sm text-gray-three">{c.text}</div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default OpsHome;
